// Quick Access Slide-In Panel
import React, { useMemo } from 'react';
import { X, Calendar, CheckSquare, MessageCircle, Clock, Bot, StickyNote, AlertCircle, CheckCircle } from 'lucide-react';

interface OrganizerItem {
  id: string;
  title: string;
  dueDate?: string;
  status: string;
  category?: string;
  description?: string;
}

interface CategoryBuckets {
  upcoming?: OrganizerItem[];
  completed?: OrganizerItem[];
}

interface Category {
  id: string;
  label: string;
  buckets: CategoryBuckets;
}

interface ClassNotebook {
  id: string;
  title: string;
  categories: Category[];
}

interface QuickAccessPanelProps {
  isOpen: boolean;
  onClose: () => void;
  classNotebooks: ClassNotebook[];
  unreadMessages?: number;
  onOpenCalendar?: () => void;
  onOpenMessages?: () => void;
  onOpenAI?: () => void;
  onOpenScratchPad?: () => void;
  onItemClick?: (event: OrganizerItem) => void;
  onMarkComplete?: (itemId: string) => void;
}


export default function QuickAccessPanel({
  isOpen,
  onClose,
  classNotebooks,
  unreadMessages = 0,
  onOpenCalendar,
  onOpenMessages,
  onOpenAI,
  onOpenScratchPad,
  onItemClick,
  onMarkComplete
}: QuickAccessPanelProps) {
  
  // Use useMemo to compute overdue/upcoming from classNotebooks (same as category tabs)
  const { overdueItems, upcomingItems } = useMemo(() => {
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const overdue: Array<OrganizerItem & { className: string }> = [];
    const upcoming: Array<OrganizerItem & { className: string }> = [];

    console.log('[QuickAccess] Processing classNotebooks:', classNotebooks.length, classNotebooks.map(n => n.title));

    // Filter out archived classes
    const activeNotebooks = classNotebooks.filter(notebook => !notebook.archived);
    console.log('[QuickAccess] Active (non-archived) notebooks:', activeNotebooks.length);
    
    activeNotebooks.forEach(notebook => {
      console.log(`[QuickAccess] Processing notebook: ${notebook.title}, categories: ${notebook.categories?.length || 0}`);
      
      if (!notebook.categories || notebook.categories.length === 0) {
        console.log(`[QuickAccess] Skipping ${notebook.title} - no categories`);
        return;
      }
      
      notebook.categories.forEach(category => {
        // Skip Virtual Room Notes, Video, and any transcript-related categories
        const categoryLower = category.label?.toLowerCase() || '';
        const categoryId = category.id?.toLowerCase() || '';
        
        if (categoryId === 'virtual-room' || 
            categoryId === 'video' ||
            categoryLower.includes('virtual room') ||
            categoryLower.includes('video') ||
            categoryLower.includes('transcript')) {
          console.log('[QuickAccess] Skipping category:', category.label, category.id);
          return;
        }
        
        const items = category.buckets?.upcoming || [];
        console.log(`[QuickAccess] ${notebook.title} - ${category.label}: ${items.length} items`);
        
        items.forEach(item => {
          // Filter out beta test items and items without due dates
          if (!item.dueDate || item.status !== 'upcoming') return;
          
          // Filter out ALL transcript/virtual room items by title and description
          const titleLower = item.title?.toLowerCase() || '';
          const descLower = item.description?.toLowerCase() || '';
          
          const isTranscript = titleLower.includes('beta test') ||
                              titleLower.includes('experiential session') ||
                              titleLower.includes('virtual room') ||
                              titleLower.includes('transcript') ||
                              descLower.includes('beta test') ||
                              descLower.includes('virtual room') ||
                              descLower.includes('transcript');
          
          if (isTranscript) {
            console.log('[QuickAccess] Filtering out transcript:', item.title);
            return;
          }
          
          const itemWithClass = { ...item, className: notebook.title, category: category.label };
          const dueDate = new Date(item.dueDate);
          
          if (dueDate < now) {
            overdue.push(itemWithClass);
          } else if (dueDate <= sevenDaysFromNow) {
            upcoming.push(itemWithClass);
          }
        });
      });
    });

    console.log('[QuickAccess] Final counts - Overdue:', overdue.length, 'Upcoming:', upcoming.length);

    // Sort by due date
    overdue.sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime());
    upcoming.sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime());

    return { overdueItems: overdue, upcomingItems: upcoming };
  }, [classNotebooks]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-full w-full sm:w-96 bg-gradient-to-b from-[#0a0513] via-[#0f051a] to-[#1a0a2e] border-l-2 border-purple-400/30 shadow-2xl z-[9999] transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-purple-400/20 bg-gradient-to-r from-purple-600/20 to-indigo-600/20">
          <div>
            <h2 className="text-xl font-bold text-white">Quick Access</h2>
            <p className="text-xs text-purple-300">Press Ctrl+Shift+W to toggle</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5 text-purple-300" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-80px)] p-6 space-y-6">
          {/* Quick Actions - 4 Buttons at TOP */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-5 h-5 text-green-400" />
              <h3 className="text-lg font-semibold text-white">Quick Actions</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={() => {
                  onOpenCalendar?.();
                  onClose();
                }}
                className="p-3 rounded-lg bg-gradient-to-br from-purple-600/20 to-indigo-600/20 border border-purple-400/30 hover:from-purple-600/30 hover:to-indigo-600/30 transition-all"
              >
                <Calendar className="w-5 h-5 text-purple-300 mx-auto mb-1" />
                <p className="text-xs text-white font-medium">Calendar</p>
              </button>
              <button 
                data-tutorial="messaging"
                onClick={() => {
                  onOpenMessages?.();
                  onClose();
                }}
                className="p-3 rounded-lg bg-gradient-to-br from-cyan-600/20 to-blue-600/20 border border-cyan-400/30 hover:from-cyan-600/30 hover:to-blue-600/30 transition-all"
              >
                <MessageCircle className="w-5 h-5 text-cyan-300 mx-auto mb-1" />
                <p className="text-xs text-white font-medium">Messages</p>
              </button>
              <button 
                onClick={() => {
                  onOpenAI?.();
                  onClose();
                }}
                className="p-3 rounded-lg bg-gradient-to-br from-emerald-600/20 to-teal-600/20 border border-emerald-400/30 hover:from-emerald-600/30 hover:to-teal-600/30 transition-all"
              >
                <Bot className="w-5 h-5 text-emerald-300 mx-auto mb-1" />
                <p className="text-xs text-white font-medium">AI Chat & Search</p>
              </button>
              <button 
                onClick={() => {
                  onOpenScratchPad?.();
                  onClose();
                }}
                className="p-3 rounded-lg bg-gradient-to-br from-amber-600/20 to-orange-600/20 border border-amber-400/30 hover:from-amber-600/30 hover:to-orange-600/30 transition-all"
              >
                <StickyNote className="w-5 h-5 text-amber-300 mx-auto mb-1" />
                <p className="text-xs text-white font-medium">Scratch Pad</p>
              </button>
            </div>
          </section>

          {/* Messages Section */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-5 h-5 text-purple-400" />
              <h3 className="text-lg font-semibold text-white">Messages</h3>
            </div>
            {unreadMessages > 0 ? (
              <div className="p-4 rounded-xl bg-purple-500/20 border border-purple-400/30">
                <p className="text-white font-semibold">
                  {unreadMessages} Unread Message{unreadMessages !== 1 ? 's' : ''}
                </p>
                <p className="text-sm text-purple-200 mt-1">
                  Check your inbox to stay connected
                </p>
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-600/20 text-center">
                <p className="text-sm text-slate-400">All caught up! ✅</p>
              </div>
            )}
          </section>

          {/* No Classes - show helpful message instead of spinner */}
          {classNotebooks.length === 0 && (
            <div className="text-center py-8">
              <p className="text-slate-400 text-sm">No classes found for this semester.</p>
              <p className="text-slate-500 text-xs mt-1">Create a class to get started!</p>
            </div>
          )}
          
          {/* No Active Classes - all archived */}
          {classNotebooks.length > 0 && classNotebooks.filter(nb => !nb.archived).length === 0 && (
            <div className="text-center py-8">
              <p className="text-slate-400 text-sm">All classes are archived.</p>
              <p className="text-slate-500 text-xs mt-1">Unarchive a class to see items here.</p>
            </div>
          )}
          
          {/* No Items in Active Classes */}
          {classNotebooks.filter(nb => !nb.archived).length > 0 && overdueItems.length === 0 && upcomingItems.length === 0 && (
            <div className="text-center py-8">
              <p className="text-slate-400 text-sm">No upcoming or overdue items.</p>
              <p className="text-slate-500 text-xs mt-1">You're all caught up! 🎉</p>
            </div>
          )}

          {/* Overdue Items */}
          {overdueItems.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <h3 className="text-lg font-semibold text-white">🔴 OVERDUE ({overdueItems.length})</h3>
              </div>
              <div className="space-y-2">
                {overdueItems.map((item) => (
                  <div
                    key={item.id}
                    className="p-3 rounded-lg bg-red-500/20 border border-red-400/40 hover:bg-red-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <button
                        onClick={() => onItemClick?.(item)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <p className="text-white font-medium truncate">{item.title}</p>
                        <p className="text-xs text-red-200 mt-1">
                          [{item.category}] {item.className}
                        </p>
                        <p className="text-xs text-red-300 mt-1">
                          Due: {new Date(item.dueDate!).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkComplete?.(item.id);
                        }}
                        className="p-2 rounded-lg bg-green-500/20 border border-green-400/30 hover:bg-green-500/30 transition-colors flex-shrink-0"
                        title="Mark as complete"
                      >
                        <CheckCircle className="w-4 h-4 text-green-300" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Upcoming Items (Next 7 Days) */}
          {upcomingItems.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <CheckSquare className="w-5 h-5 text-cyan-400" />
                <h3 className="text-lg font-semibold text-white">📅 DUE THIS WEEK ({upcomingItems.length})</h3>
              </div>
              <div className="space-y-2">
                {upcomingItems.map((item) => (
                  <div
                    key={item.id}
                    className="p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <button
                        onClick={() => onItemClick?.(item)}
                        className="flex-1 min-w-0 text-left"
                      >
                        <p className="text-white font-medium truncate">{item.title}</p>
                        <p className="text-xs text-purple-300 mt-1">
                          [{item.category}] {item.className}
                        </p>
                        <p className="text-xs text-cyan-300 mt-1">
                          Due: {new Date(item.dueDate!).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onMarkComplete?.(item.id);
                        }}
                        className="p-2 rounded-lg bg-green-500/20 border border-green-400/30 hover:bg-green-500/30 transition-colors flex-shrink-0"
                        title="Mark as complete"
                      >
                        <CheckCircle className="w-4 h-4 text-green-300" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Empty State */}
          {classNotebooks.length > 0 && overdueItems.length === 0 && upcomingItems.length === 0 && (
            <section>
              <div className="p-8 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-400/20 text-center">
                <p className="text-xl mb-2">🎉</p>
                <p className="text-white font-semibold mb-1">You're all caught up!</p>
                <p className="text-sm text-green-200">
                  No items due in the next week across all your classes
                </p>
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
}
